import { useMutation } from '@apollo/client';
import { useLocation, useNavigate } from '@reach/router';
import { useIntl } from 'react-intl';

import { useSearchParameters } from '@xing-com/crate-hooks-use-search-parameters';
import {
  MAX_SALARY,
  MIN_SALARY,
  SEARCH_ALERTS_EMAIL_BANNER_STATE,
  SEARCH_ALERTS_MODAL_PARAM,
  SEARCH_STATE_PARAMS,
  SEARCH_STATE_SEPARATOR,
} from '@xing-com/crate-jobs-constants';
import { CreateSearchAlertForLoDocument } from '@xing-com/crate-jobs-graphql';
import { unscrambleStringId } from '@xing-com/crate-utils-x1-data';

type UseLOContent = {
  stringifiedQuery: string;
  createSearchAlert: (
    email: string,
    setError: React.Dispatch<React.SetStateAction<string>>
  ) => void;
};

export const useLOContent = (): UseLOContent => {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [createLOSearchAlert] = useMutation(CreateSearchAlertForLoDocument);
  const { getSearchParameters } = useSearchParameters();

  const {
    [SEARCH_STATE_PARAMS.keywords]: keywords,
    [SEARCH_STATE_PARAMS.location]: location,
    [SEARCH_STATE_PARAMS.radius]: radius,
    [SEARCH_STATE_PARAMS.benefit]: benefit,
    [SEARCH_STATE_PARAMS.careerLevel]: level,
    [SEARCH_STATE_PARAMS.city]: city,
    [SEARCH_STATE_PARAMS.country]: country,
    [SEARCH_STATE_PARAMS.discipline]: discipline,
    [SEARCH_STATE_PARAMS.employmentType]: employmentType,
    [SEARCH_STATE_PARAMS.industry]: industry,
    [SEARCH_STATE_PARAMS.remoteOption]: remoteOption,
    [SEARCH_STATE_PARAMS.salary]: salary,
  } = getSearchParameters([
    SEARCH_STATE_PARAMS.keywords,
    SEARCH_STATE_PARAMS.location,
    SEARCH_STATE_PARAMS.radius,
    SEARCH_STATE_PARAMS.benefit,
    SEARCH_STATE_PARAMS.careerLevel,
    SEARCH_STATE_PARAMS.city,
    SEARCH_STATE_PARAMS.country,
    SEARCH_STATE_PARAMS.discipline,
    SEARCH_STATE_PARAMS.employmentType,
    SEARCH_STATE_PARAMS.industry,
    SEARCH_STATE_PARAMS.remoteOption,
    SEARCH_STATE_PARAMS.salary,
  ]);

  const stringifiedQuery = `${keywords ?? ''}${
    keywords && location ? ', ' : ''
  }${location ?? ''}${location && radius ? ` (${radius} km)` : ''}`;

  const stringToIds = (filter: string | null): string[] | undefined => {
    if (!filter) {
      return;
    }

    const ids = filter.split(SEARCH_STATE_SEPARATOR).map(unscrambleStringId);
    return ids.length ? ids : undefined;
  };

  const createSearchAlert: UseLOContent['createSearchAlert'] = (
    email,
    setError
  ) => {
    const salaryValue = salary?.split('_') ?? [];
    const isDefaultSalary =
      (salaryValue[0] === `${MIN_SALARY}` &&
        salaryValue[1] === `${MAX_SALARY}`) ||
      (!salaryValue[0] && !salaryValue[1]);

    createLOSearchAlert({
      variables: {
        email,
        query: {
          keywords,
          location,
          radius,
          filters: {
            benefits: stringToIds(benefit),
            level: stringToIds(level),
            city: stringToIds(city),
            country: stringToIds(country),
            discipline: stringToIds(discipline),
            type: stringToIds(employmentType),
            industry: stringToIds(industry),
            remoteOption: stringToIds(remoteOption),
            salary: isDefaultSalary
              ? undefined
              : [salaryValue[0], salaryValue[1]],
          },
        },
      },
      onCompleted: ({ createSearchAlertForLO }) => {
        const isSuccess =
          createSearchAlertForLO?.__typename ===
          'SearchAlertForLOCreationSuccess';

        if (isSuccess) {
          const params = new URLSearchParams(search);
          params.delete(SEARCH_ALERTS_MODAL_PARAM);

          const message = {
            __typename: 'LOSearchAlertMessage',
            title: formatMessage({
              id: 'JOBS_SEARCH_ALERT_LO_MODAL_CONFIRMATION_BANNER_TITLE',
            }),
            body: formatMessage(
              {
                id: 'JOBS_SEARCH_ALERT_LO_MODAL_CONFIRMATION_BANNER_BODY',
              },
              { email }
            ),
          };

          navigate(`${pathname}?${params.toString()}`, {
            replace: true,
            state: { [SEARCH_ALERTS_EMAIL_BANNER_STATE]: message },
          });
        } else {
          setError(
            formatMessage({
              id: 'JOBS_SEARCH_ALERT_CREATION_ERROR_BANNER_TEXT',
            })
          );
        }
      },
      onError: () => {
        setError(
          formatMessage({ id: 'JOBS_SEARCH_ALERT_CREATION_ERROR_BANNER_TEXT' })
        );
      },
    });
  };

  return { stringifiedQuery, createSearchAlert };
};
